import React from "react";
import api from "@/lib/api";

/** FMG Advocate Orientation — thin client over the Command Center orientation
 *  API (Phase 5D). All question wording, correction explanations, gating and
 *  certification come from Command Center via the FMG backend proxy. */
export function AdvocateTraining() {
  const [data, setData] = React.useState(null);
  const [answered, setAnswered] = React.useState(new Set());
  const [ack, setAck] = React.useState(null);
  const [gate, setGate] = React.useState(null);
  const [complete, setComplete] = React.useState(false);
  const [viewIdx, setViewIdx] = React.useState(null);
  const [localAnswers, setLocalAnswers] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [unavailable, setUnavailable] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const call = async (payload) => {
    setBusy(true); setErr("");
    try {
      const { data: body } = await api.post("/advocate/training", payload);
      if (body?.status === "error" || body?.error) {
        setErr(body?.message || body?.error || "Orientation request was rejected. Please try again.");
        return null;
      }
      return body?.data ?? body;
    } catch (e) {
      if (e?.response?.status === 503) setUnavailable(true);
      else setErr(e?.response?.data?.detail || "Orientation request failed. Please try again.");
      return null;
    } finally { setBusy(false); }
  };

  const qid = (q) => q.question_id ?? q.id;
  const load = React.useCallback(async () => {
    // Status first: certification survives reloads via Command Center's
    // persisted orientation status, not the questions list.
    const s = await call({ action: "get_orientation_status" });
    const sCertified = !!s && (s.certification_status === "certified" || s.certified === true);
    if (sCertified) { setData(s); setComplete(true); return; }
    const d = await call({ action: "get_orientation_questions" });
    if (!d) { if (s) setData(s); return; }
    setData({ ...(s || {}), ...d });
    const qs = d.questions || [];
    const ids = new Set();
    qs.forEach((q) => { if (q.answered === true || q.response != null || q.completed === true) ids.add(qid(q)); });
    (d.responses || []).forEach((r) => { if (r.question_id != null) ids.add(r.question_id); });
    setAnswered(ids);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const h = () => {
      setOpen(true);
      setTimeout(() => document.querySelector('[data-testid="advocate-training"]')?.scrollIntoView({ behavior: "smooth" }), 120);
    };
    window.addEventListener("fmg-open-orientation", h);
    return () => window.removeEventListener("fmg-open-orientation", h);
  }, []);

  if (unavailable) return null;
  if (!data) return null;

  const questions = data.questions || [];
  const total = questions.length || 10;
  const doneCount = questions.filter((q) => answered.has(qid(q))).length;
  const currentIdx = questions.findIndex((q) => !answered.has(qid(q)));
  const current = currentIdx >= 0 ? questions[currentIdx] : null;
  const idx = viewIdx !== null && viewIdx >= 0 && viewIdx < questions.length ? viewIdx : currentIdx;
  const viewing = idx >= 0 ? questions[idx] : null;
  const isReview = !!viewing && answered.has(qid(viewing));
  const savedAnswer = (q) => {
    const v = localAnswers[qid(q)] ?? q.response ?? q.answer ?? q.member_answer;
    if (v == null) return "Answered";
    const s = String(v);
    return s === "acknowledged" ? "I Understand (acknowledged)" : s.charAt(0).toUpperCase() + s.slice(1);
  };
  const goPrevious = () => { if (idx > 0) setViewIdx(idx - 1); };
  const goNext = () => {
    const n = idx + 1;
    if (currentIdx !== -1 && n >= currentIdx) setViewIdx(null);
    else if (n < questions.length) setViewIdx(n);
    else setViewIdx(null);
  };
  const trainingStatus = data.training_status || data.status || (doneCount === 0 ? "not_started" : "in_progress");
  const certStatus = data.certification_status || "not_certified";
  const certified = complete || certStatus === "certified" || data.certified === true || trainingStatus === "completed";
  const qText = (q) => q.question_text || q.text || q.question || q.prompt;

  const finish = async () => {
    const d = await call({ action: "complete_orientation" });
    if (d) {
      if (d.gate_failed) { setGate(d); return; }
      setComplete(true);
    }
  };

  const advance = (id) => {
    const next = new Set(answered); next.add(id); setAnswered(next);
    const remaining = questions.filter((q) => !next.has(qid(q))).length;
    if (remaining === 0) finish();
  };

  const submit = async (q, ans, acknowledged) => {
    const payload = { action: "submit_orientation_response", question_id: qid(q), answer: ans };
    if (acknowledged) payload.acknowledged_correction = true;
    const d = await call(payload);
    if (!d) return;
    if (d.gate_failed) { setAck(null); setGate(d); return; }
    if (d.needs_acknowledgment) {
      setAck({ q, ans, explanation: d.explanation || d.correction || d.message || "Please review the correction before continuing." });
      return;
    }
    setAck(null);
    setLocalAnswers((prev) => ({ ...prev, [qid(q)]: ans }));
    setViewIdx(null);
    advance(qid(q));
  };

  if (!open) {
    const entryLabel = certified ? "View Certification" : doneCount === 0 ? "Start Training" : "Continue Training";
    return (
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="advocate-training">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold tracking-widest text-red-700 uppercase">Advocate Training</div>
            <h3 className="mt-1 font-black font-display text-slate-900 text-lg">FMG Advocate Orientation</h3>
            {!certified && total > 0 && (
              <p className="mt-1 text-sm text-slate-600" data-testid="training-progress">{doneCount} of {total} items complete</p>
            )}
          </div>
          <button onClick={() => setOpen(true)} data-testid="training-entry-btn"
                  className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded text-sm font-semibold">
            {entryLabel}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="advocate-training">
      <div className="text-xs font-semibold tracking-widest text-red-700 uppercase">Advocate Training</div>
      <div className="mt-1 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-black font-display text-slate-900 text-lg">FMG Advocate Orientation</h3>
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-slate-100 text-slate-700" data-testid="training-status">
            Training: {certified ? "Completed" : doneCount === 0 ? "Not Started" : "In Progress"}
          </span>
          <span className={`px-2 py-1 rounded ${certified ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`} data-testid="certification-status">
            {certified ? "Certification: Certified" : "Certification: Not Certified"}
          </span>
        </div>
      </div>
      {err && <p className="mt-2 text-sm text-red-600" data-testid="training-error">{err}</p>}

      {!certified && !gate && total > 0 && (
        <div className="mt-3">
          <div className="text-xs text-slate-500" data-testid="orientation-progress">{doneCount} of {total} items complete</div>
          <div className="mt-1 h-2 bg-slate-100 rounded overflow-hidden">
            <div className="h-full bg-red-700 transition-all" style={{ width: `${(doneCount / total) * 100}%` }} />
          </div>
        </div>
      )}

      {certified && (
        <div className="mt-4 p-5 bg-green-50 border border-green-200 rounded" data-testid="orientation-complete">
          <div className="font-bold text-green-900">FMG Advocate Orientation Complete</div>
          <p className="mt-2 text-sm text-green-800">You understand what FileMyGrievance is, what it is not, and your role as an FMG Advocate.</p>
          <p className="mt-2 text-sm font-semibold text-green-900">You are now a Certified FMG Advocate.</p>
        </div>
      )}

      {!certified && gate && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-800" data-testid="gate-failed">
          <div className="font-semibold">Orientation cannot continue</div>
          <p className="mt-1">{gate.message || gate.explanation || gate.reason || "Command Center has stopped this orientation. Please contact support for next steps."}</p>
        </div>
      )}

      {!certified && !gate && ack && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded" data-testid="orientation-correction">
          <div className="text-sm font-semibold text-amber-900">Please review</div>
          <p className="mt-1 text-sm text-amber-900 whitespace-pre-line">{ack.explanation}</p>
          <button onClick={() => submit(ack.q, ack.ans, true)} disabled={busy} data-testid="acknowledge-btn"
                  className="mt-3 px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded text-sm font-semibold disabled:opacity-50">
            I Understand
          </button>
        </div>
      )}

      {!certified && !gate && !ack && viewing && (
        <div className="mt-4 border border-slate-200 rounded p-5" data-testid="orientation-question">
          <div className="text-xs font-semibold text-slate-500" data-testid="question-counter">Item {idx + 1} of {total}</div>
          <p className="mt-2 text-slate-900 font-medium" data-testid="question-text">{qText(viewing)}</p>
          {isReview ? (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm" data-testid="saved-answer">
                Your answer: <span className="font-semibold">{savedAnswer(viewing)}</span>
              </span>
              <button onClick={goNext} data-testid="next-item-btn"
                      className="px-6 py-2 bg-red-700 hover:bg-red-800 text-white rounded text-sm font-semibold">Next</button>
            </div>
          ) : viewing.question_type === "acknowledgment" ? (
            <div className="mt-4">
              <button onClick={() => submit(viewing, "acknowledged")} disabled={busy} data-testid="acknowledgment-btn"
                      className="px-6 py-2 bg-red-700 hover:bg-red-800 text-white rounded text-sm font-semibold disabled:opacity-50">I Understand</button>
            </div>
          ) : (
          <div className="mt-4 flex gap-3">
            <button onClick={() => submit(viewing, "yes")} disabled={busy} data-testid="answer-yes-btn"
                    className="px-6 py-2 bg-red-700 hover:bg-red-800 text-white rounded text-sm font-semibold disabled:opacity-50">Yes</button>
            <button onClick={() => submit(viewing, "no")} disabled={busy} data-testid="answer-no-btn"
                    className="px-6 py-2 border border-slate-300 hover:bg-slate-50 text-slate-800 rounded text-sm font-semibold disabled:opacity-50">No</button>
          </div>
          )}
          {idx > 0 && (
            <button onClick={goPrevious} disabled={busy} data-testid="previous-item-btn"
                    className="mt-4 text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-50">
              ← Previous
            </button>
          )}
        </div>
      )}

      {!certified && !gate && !ack && !current && total > 0 && (
        <div className="mt-4 text-sm text-slate-600" data-testid="orientation-finishing">Finalizing your orientation…</div>
      )}
    </section>
  );
}
